export interface NormalizedBrowserError {
  readonly title: string;
  readonly cause: string;
  readonly nextAction: string;
  readonly technicalDetail?: string;
}

function browserErrorDetail(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "detail" in error &&
    typeof error.detail === "string"
  ) {
    return error.detail;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function hostFromPolicyError(detail: string): string | null {
  const policyHost = detail.match(/blocked by policy for\s+([^.\s]+(?:\.[^.\s]+)*)\.?/i)?.[1];
  if (policyHost) {
    return policyHost;
  }
  return detail.match(/navigation to\s+([^.\s]+(?:\.[^.\s]+)*)\s+is restricted/i)?.[1] ?? null;
}

export function normalizeBrowserError(error: unknown): NormalizedBrowserError {
  const detail = browserErrorDetail(error).trim() || "Unknown browser error.";
  const policyHost = hostFromPolicyError(detail);

  if (/blocked by policy|restricted to allowed domains/i.test(detail)) {
    return {
      title: "Browser navigation blocked",
      cause: `${policyHost ?? "This URL"} is not allowed by the current browser policy.`,
      nextAction: "Use an allowed URL or update the browser allowed-domain setting.",
      technicalDetail: detail,
    };
  }

  if (
    /does not match thread|session .*not found|no active browser tab|tab .*not found/i.test(detail)
  ) {
    return {
      title: "Browser session is stale",
      cause: "Forge no longer has a matching live browser session for this thread.",
      nextAction: "Restart the browser session from the Browser options menu.",
      technicalDetail: detail,
    };
  }

  if (/screenshot|capture|Page\.capture/i.test(detail)) {
    return {
      title: "Browser screenshot failed",
      cause: "The browser could not capture the current page frame.",
      nextAction: "Reload the page, then try the screenshot again.",
      technicalDetail: detail,
    };
  }

  if (/timed out|timeout/i.test(detail)) {
    return {
      title: "Browser action timed out",
      cause: "Forge did not receive a response from the browser runtime in time.",
      nextAction: "Retry the action. If it keeps failing, restart the browser session.",
      technicalDetail: detail,
    };
  }

  if (/runtime is not available|browser.*unavailable|not supported|disabled/i.test(detail)) {
    return {
      title: "Browser session unavailable",
      cause: "This environment does not currently expose the in-app browser runtime.",
      nextAction:
        "Use a workspace with browser support or restart Forge after browser support is enabled.",
      technicalDetail: detail,
    };
  }

  if (/navigate|navigation|net::|ERR_/i.test(detail)) {
    return {
      title: "Browser navigation failed",
      cause: "The browser could not load the requested page.",
      nextAction: "Check the URL and network access, then try again.",
      technicalDetail: detail,
    };
  }

  return {
    title: "Browser action failed",
    cause: "Forge could not complete the browser action.",
    nextAction:
      "Retry the action. If it keeps failing, restart the browser session from the Browser options menu.",
    technicalDetail: detail,
  };
}

export function formatBrowserErrorDescription(error: unknown): string {
  const normalized = normalizeBrowserError(error);
  return `${normalized.cause} ${normalized.nextAction}`;
}

export function formatBrowserErrorForUi(error: unknown): string {
  const normalized = normalizeBrowserError(error);
  return `${normalized.title}: ${normalized.cause} ${normalized.nextAction}`;
}

export function formatBrowserErrorForTool(error: unknown): string {
  const normalized = normalizeBrowserError(error);
  return [
    `Problem: ${normalized.title}`,
    `Likely cause: ${normalized.cause}`,
    `Next action: ${normalized.nextAction}`,
    `Technical detail: ${normalized.technicalDetail}`,
  ].join("\n");
}
